package com.dynamicruntime.common.user

import com.dynamicruntime.common.http.request.ROLE
import com.dynamicruntime.common.http.request.RoleLadder

// Auth/profile constants that the *frontend* (Kotlin/JS) shares with the backend: endpoint paths, request and
// response field (JSON key) names, UI-config feature flags, schema type names, and fragment file ids. They
// live in the KMP kernel (not base:common) precisely so the transpiled frontend can reference the same
// strings the backend serves, instead of re-hardcoding them. Per the code guide, these are lowerCamelCase
// `const val`s scoped in short upper-case acronym objects, always referenced qualified.

/** Auth and profile endpoint paths (as the frontend calls them, before the API context root is prepended). */
@Suppress("ConstPropertyName")
object AEP {
    const val createToken = "/auth/form/createToken"
    const val newContactSendVerify = "/auth/newContact/sendVerify"
    const val userSendVerify = "/auth/user/sendVerify"
    const val createInitial = "/auth/user/createInitial"
    const val setLoginData = "/auth/user/setLoginData"
    const val loginByCode = "/auth/login/byCode"
    const val loginByPassword = "/auth/login/byPassword"
    const val loginByGoogle = "/auth/login/google"
    const val setPassword = "/auth/user/setPassword"
    const val authUiConfig = "/auth/ui/config"
    const val selfInfo = "/auth/self/info"
    /**
     * Switch from inside a sandbox to another of the person's users (issue #1197); a fresh session cookie, as
     * [switchUser] issues. In the anonymous `auth` section rather than beside [switchUser] in the gated `user` one,
     * because it is for the session the gate refuses: a sandbox user nobody stands behind any more acts with no
     * roles at all, and must still be able to go back the way it came.
     */
    const val leaveSandbox = "/auth/self/leaveSandbox"
    const val logout = "/logout"
    const val profileUiConfig = "/profile/ui/config"
    const val profileClearPassword = "/profile/self/clearPassword"
    const val profileSetName = "/profile/self/setName"

    // The switcher (issue #749), in the login-gated `user` section rather than the anonymous `auth` one: every
    // call acts on the session's identity, so the gate -- which also re-reads the acting row -- is the right
    // one, and there is nothing here for a logged-out caller.
    /** The users the signed-in person may act as. */
    const val selfUsers = "/user/self/users"
    /** Become another of the person's users; issues a fresh session cookie. */
    const val switchUser = "/user/self/switch"
    /** Become the person's user in a named client (issue #1110); issues a fresh session cookie. */
    const val switchClient = "/user/self/switchClient"
    /**
     * Create the person's one self-serve client (issue #1113), from `public`, as its administrator; issues a fresh
     * session cookie in it.
     */
    const val createOwnClient = "/user/self/createClient"
    /** Choose which of the person's users an address logs in as. */
    const val setDefaultUser = "/user/self/setDefault"
    /**
     * Permanently remove one of the person's own `public` users (issue #752) -- the placeholder a person holds
     * before anybody places them in a real client -- up to and including their last user.
     */
    const val removePublicUser = "/user/self/removePublic"
    /**
     * Open a client's Shadow Sandbox (issue #929): become the caller's own user in it -- created on first use, under
     * the same identity -- which then acts as the caller's user outside it, with a fresh session. For an
     * administrator of the parent, or an `allClients` administrator for any client with a sandbox.
     */
    const val openSandbox = "/user/self/openSandbox"

    // Invitations (issue #751), anonymous like the rest of the register/login flow: the mailed link is the
    // proof, and the person opening it has no session yet.
    /** What an invitation token is for -- the address, client, and persona -- before it is accepted. */
    const val invitationPreview = "/auth/invitation/preview"
    /** Accept an invitation: proves the address, registers the invited user, and logs in as it. */
    const val invitationAccept = "/auth/invitation/accept"

    // Claiming an account created for you (issue #751), from the login page with nothing but what the
    // invitation said: the address, the client and the persona name the user; a mailed code proves the address.
    /** Mails a verification code for the user the address, client, and persona name -- or a mail saying no user matches. */
    const val claimSendVerify = "/auth/claim/sendVerify"
    /** Registers (and logs in as) the user the address, client, and persona name, with the mailed code. */
    const val claimAccount = "/auth/claim/register"
}

/** Auth request/response field (JSON key) names, shared so the frontend builds and reads payloads by constant. */
@Suppress("ConstPropertyName")
object AFLD {
    const val username = "username"

    /** A login identifier for looking up an existing account: a username **or** an email address. */
    const val loginId = "loginId"

    /** The profile config's history of the signed-in user (issue #1480): an `AccountHistory`. */
    const val account = "account"
    const val password = "password"
    const val formAuthToken = "formAuthToken"
    const val verifyCode = "verifyCode"
    const val contactAddress = "contactAddress"
    const val contactType = "contactType"
    const val addPassword = "addPassword"
    const val userId = "userId"

    /** Registration flag: this account belongs to a business, not a person (sets `authUserData.isEntity`). */
    const val isEntity = "isEntity"

    /**
     * The account's real-world (non-unique) name, captured at registration: the registrant's full name, or the
     * business's name when [isEntity] is set.
     */
    const val name = "name"

    /** The Google ID token (a JWT) the browser's Google sign-in hands back, POSTed to [AEP.loginByGoogle]. */
    const val googleCredential = "googleCredential"

    /**
     * The Google OAuth **client id**. Public by design (it identifies the application to Google, and the
     * browser must present it), so the auth UI config carries it to the frontend; the deployment sets it with
     * `KDR_GOOGLE_CLIENT_ID`. Empty when Google sign-in is not configured.
     */
    const val googleClientId = "googleClientId"

    /** The `state.userInfo` key of a UI-config payload (the caller's user info). */
    const val userInfo = "userInfo"

    /** The `items` of the users list (issue #749): the caller's `UserChoice`s. */
    const val users = "users"

    /**
     * On registration (issue #751), for an `allClients` caller only: which client, persona, and personaSuffix the
     * new user takes. Anyone else registers into `public` as a member, and naming one of these is refused.
     */
    const val client = "client"
    const val persona = "persona"
    const val personaSuffix = "personaSuffix"

    /** The invited user's client's display name (issue #1438), beside its id: what the invitation page says. */
    const val clientName = "clientName"

    /** The invitation token (issue #751): the encrypted (identityId, userId, expiry) the mailed link carries. */
    const val invitationToken = "invitationToken"

    /** On the invitation preview: the invited address (the user's name rides under [name]). */
    const val email = "email"
}

/** UI-config feature-flag keys for the auth and profile widget-groups. */
@Suppress("ConstPropertyName")
object AFEAT {
    const val registration = "registration"
    const val codeLogin = "codeLogin"
    const val passwordLogin = "passwordLogin"

    /** Whether Google sign-in is offered (on only when the deployment configured a client id). */
    const val googleLogin = "googleLogin"
    const val hasPassword = "hasPassword"
    const val canSetPassword = "canSetPassword"

    /**
     * Whether the frontend may read a verification code back from `/fixture/simulatedEmails`: email is simulated
     * **and** the caller has test access (a test instance, with env auth effective -- issue #1149), for whom alone
     * that fixture exists. Its only readers are the code pre-fills.
     */
    const val codeReadBack = "codeReadBack"
}

/** Auth/profile schema type names (the backend's output/input type refs; also useful to the frontend). */
@Suppress("ConstPropertyName")
object ATYPE {
    const val formToken = "FormToken"
    const val authAck = "AuthAck"
    const val userIdResult = "UserIdResult"
    const val authUiConfig = "AuthUiConfig"
    const val profileUiConfig = "ProfileUiConfig"
    /** The users list (issue #749): the caller's `UserChoice`s under `users`. */
    const val userChoices = "UserChoices"
    /** What an invitation is for (issue #751): address, client, persona, personaSuffix, name. */
    const val invitationInfo = "InvitationInfo"
}

/** Markdown fragment file ids for the auth-area widget-groups (each also the group's fragment namespace). */
@Suppress("ConstPropertyName")
object AFRAG {
    const val auth = "auth"
    const val profile = "profile"

    /**
     * The mails the auth flows send (issue #773): a **backend** file, never served, whose namespaces are one
     * per mail (`MailCopy` in `base:common` reads it). Here beside the delivered files because a client
     * overlays it the same way, through its config.
     */
    const val mail = "mail"
}

/**
 * Keys of the `error` namespace in the `auth` fragment file: the copy for auth error messages (issue #108).
 * The backend renders these from `KdrException`'s `KdrMsg` at the top-level handler -- the sentence lives here
 * (in `auth.md`), not duplicated in Kotlin. The `${...}`-bearing ones take the noted params.
 */
@Suppress("ConstPropertyName")
object AERR {
    const val ns = "error"

    // Parameter-free.
    const val codeIncorrect = "codeIncorrect"
    const val tokenExpired = "tokenExpired"
    /** The address is not a well-formed email (issue #743; was `emailNoAt`, which checked only for an `@`). */
    const val emailInvalid = "emailInvalid"
    const val loginFailed = "loginFailed"
    const val tooManyVerifyAttempts = "tooManyVerifyAttempts"
    const val tooManyVerifyRequests = "tooManyVerifyRequests"
    const val tooManyLoginAttempts = "tooManyLoginAttempts"

    /**
     * A public test deployment refuses this caller the sign-in flows (issue #1150): they have no env auth, and the
     * deployment lets only env-authed people sign in.
     */
    const val testDeploymentSignIn = "testDeploymentSignIn"

    /** Google sign-in was attempted but the deployment has no client id configured. */
    const val googleNotConfigured = "googleNotConfigured"

    /** The Google ID token did not verify (bad signature, wrong audience/issuer, or expired). */
    const val googleTokenInvalid = "googleTokenInvalid"

    /** Google supplied the account's email but has not itself verified it, so it cannot identify a user. */
    const val googleEmailUnverified = "googleEmailUnverified"

    // `noAccount` was removed with issue #275: it named an account by whether it exists, and an auth entry
    // point that answers "no account was found" is a membership oracle. The login/send-verify paths now fail
    // *identically* whether or not the account exists (see AuthFormHandler), so there is nothing to say here
    // -- and the message is deliberately gone rather than merely unused, so it cannot be wired back in.

    /** Registration attempted for an already-taken email. Param `email`; sensitive (obfuscated in prod). */
    const val emailNotAvailable = "emailNotAvailable"

    /** Param key the `${...}` placeholder in [emailNotAvailable] references. */
    const val emailParam = "email"

    /**
     * A user with the same (address, client, persona, personaSuffix) already exists (issue #750). Also the
     * envelope's logical `errorCode`, which is what the console keys on to offer the personaSuffix box -- the
     * wording is free to change. Params: [emailParam], [clientParam], [personaParam], [personaSuffixNoteParam].
     */
    const val userKeyTaken = "userKeyTaken"
    const val clientParam = "client"
    const val personaParam = "persona"

    /** The invitation link is malformed, tampered with, expired, or names a user that no longer fits it (issue #751). */
    const val invitationInvalid = "invitationInvalid"

    /** The invitation was already accepted: the user is registered, and a mailed link is not a standing login. */
    const val invitationUsed = "invitationUsed"
    /** `, personaSuffix 'B'` when the key carried one, else empty -- so one sentence serves both. */
    const val personaSuffixNoteParam = "personaSuffixNote"
}

/**
 * One persona a user may be created with (issues #747, #750): its wire [name], the [label] a person sees, and
 * the [defaultRoles] a user of it starts with. The roles are applied **at creation only** -- after that they
 * are whatever an administrator makes them, the way the auto-admin rule works -- and never include a capability:
 * a persona never carries `allClients` (see `PERSONA`). Ladder roles, and `editor` (issue #1432), which confers
 * reach within the user's own client and is no capability.
 */
class PersonaDef(val name: String, val label: String, val defaultRoles: List<String>)

/**
 * The personas a user may be created with (issue #747): what relationship the user has to the application,
 * frozen at creation and part of the user's unique key with its identity, client and `personaSuffix`. A registry
 * (issue #750) of two to start; a client may later add its own under `docs/design/client-definition.md`'s reservation
 * of the word. A persona is deliberately **not** a role -- `admin` here says how to read the user, and the roles say
 * what they may do -- though each names the roles a user of it *starts* with.
 *
 * The default persona is **`member`** (Sam, 2026-09-18), not `user`: what it says is that the person belongs
 * to the client, which pairs naturally with `admin` and the later `reviewer` and `advisor`, and it stays clear
 * of the *role* `user`, a rung on the privilege ladder on a different axis. (The alternatives -- standard,
 * regular, ordinary -- read as tiers or carry an edge.)
 *
 * In the kernel so the console offers the same choices, with the same labels, that the backend accepts.
 */
@Suppress("ConstPropertyName")
object PERSONA {
    const val member = "member"
    const val admin = "admin"

    /**
     * Sees every user and form of its client and edits forms, administering nothing (issue #1432): a user created as
     * one starts with [ROLE.editor]. The name says the relationship -- someone who works on the client's forms -- as
     * `member` and `admin` do theirs.
     */
    const val editor = "editor"

    /**
     * The registry, in the order a chooser offers them. **No capability appears in a default role list, by
     * construction**: `allClients` is a grant an administrator makes to a user, never a property of what kind
     * of user they are, and a persona that could carry it would be the escalation route the email tags were
     * retired to close.
     */
    val defs: List<PersonaDef> = listOf(
        PersonaDef(member, "Member", listOf(ROLE.user)),
        PersonaDef(editor, "Editor", listOf(ROLE.user, ROLE.editor)),
        PersonaDef(admin, "Admin", listOf(ROLE.user, ROLE.admin)),
    )

    /** The registry by name. */
    val byName: Map<String, PersonaDef> = defs.associateBy { it.name }

    /** The persona named [persona], or null when the registry holds none of that name. */
    fun def(persona: String): PersonaDef? = byName[persona]

    /** How a persona is shown: its registered label, or the name capitalized for one the registry does not hold. */
    fun label(persona: String): String = def(persona)?.label ?: persona.replaceFirstChar { it.uppercaseChar() }

    /**
     * The persona a user created with [roles] takes when none is named (Sam, 2026-09-18): personas grant roles
     * by default, and conversely roles provide a default persona. The registry entry whose default roles put a
     * user on the same rung of `RoleLadder` as [roles] do -- `admin` for an administrator, `member` otherwise
     * (an operator, whose rung no persona names, is a member). So the deployment's first administrator, the
     * fixture's admin-level user and an admin created with an explicit role list all read as what they are.
     * An editor who is no administrator is an [editor] (issue #1432): the role, off the ladder, shares the member's
     * rung, so it is asked first, and the rung match below passes over the persona.
     */
    fun defaultFor(roles: Collection<String>): String {
        if (ROLE.editor in roles && ROLE.admin !in roles) return editor
        val rung = RoleLadder.highestHeld(roles)
        return defs.lastOrNull { it.name != editor && RoleLadder.highestHeld(it.defaultRoles) == rung }?.name ?: member
    }

    /** [accessOf]'s answer for a role list that gives no access at all: a disabled user's, or a stranded sandbox user's. */
    const val noAccess = "none"

    /** [accessOf]'s answer for an operator who is also an editor: the two reaches are separate, and neither holds the other. */
    const val operatorAndEditor = "operatorEditor"

    /**
     * The access [roles] give, read as one answer (issue #1450): the highest rung of `RoleLadder` held -- an `admin`,
     * which implies `editor`, is just an admin -- with `editor` beside a rung below it, since an operator holds no
     * part of editor nor an editor of operator ([operatorAndEditor]). [noAccess] for a list holding neither a rung
     * nor `editor`: the empty list a disabled or stranded user acts with. A capability (`allClients`) and a
     * deployment's own role are no access level and change nothing here.
     */
    fun accessOf(roles: Collection<String>): String {
        val rung = RoleLadder.highestHeld(roles)
        val editor = ROLE.editor in roles
        return when {
            rung == ROLE.admin -> ROLE.admin
            rung == ROLE.operator && editor -> operatorAndEditor
            editor -> ROLE.editor
            rung != null -> rung
            else -> noAccess
        }
    }

    /** How an [accessOf] answer reads in the console: `Administrator`, `Operator and editor`, `Editor`, `User`, `None`. */
    fun accessLabel(access: String): String = when (access) {
        ROLE.admin -> "Administrator"
        ROLE.operator -> "Operator"
        operatorAndEditor -> "Operator and editor"
        ROLE.editor -> "Editor"
        ROLE.user -> "User"
        noAccess -> "None"
        else -> access.replaceFirstChar { it.uppercaseChar() }
    }

    /** An [accessOf] answer as a phrase beside a persona: `Administrator access`, `No access`. */
    fun accessPhrase(access: String): String = if (access == noAccess) "No access" else "${accessLabel(access)} access"

    /**
     * The access a user of [persona] has when its [roles] give other access than the persona's own default roles do
     * (issue #1450) -- or null when the two agree, or the persona is none the registry holds. A persona names which
     * of a person's users this is, and is frozen at creation; roles say what it may do. They part when roles move
     * on -- a member promoted to administrator, an administrator demoted -- and from the start for a level no
     * persona's defaults name: an operator is a member or an admin given the rung. Either way the persona's label
     * alone would say the wrong thing.
     */
    fun actsAs(persona: String, roles: Collection<String>): String? {
        val own = def(persona)?.defaultRoles ?: return null
        return accessOf(roles).takeIf { it != accessOf(own) }
    }

    /**
     * [term] -- a persona's label, or its [personaTerm] with the suffix -- as a user of [persona] with [roles] reads
     * it: `Member (Administrator access)`, `Admin (No access)` when [actsAs] says they part, [term] alone when not, or
     * when [roles] is not known. Worded as access, never as "acts as", which elsewhere names the user a sandbox user
     * acts as.
     */
    fun withActing(term: String, persona: String, roles: Collection<String>?): String =
        roles?.let { actsAs(persona, it) }?.let { "$term (${accessPhrase(it)})" } ?: term

    /**
     * A persona as a person types it on the claim page (issue #751), `admin` or `member B`, split into the
     * persona name and the personaSuffix suffix (empty when there is none): the first word and whatever follows it,
     * trimmed. Blank is the default persona. What `typed` shows: the two joined by a space, as the invitation
     * mail spells them. Pure, in the kernel so the page and the backend read one rule.
     */
    fun splitTyped(text: String): Pair<String, String> {
        val trimmed = text.trim()
        if (trimmed.isEmpty()) return member to ""
        val space = trimmed.indexOfFirst { it.isWhitespace() }
        return if (space < 0) trimmed.lowercase() to "" else trimmed.substring(0, space).lowercase() to trimmed.substring(space + 1).trim()
    }

    /** The claim-page spelling of a persona and personaSuffix: `admin`, `member B`. */
    fun typed(persona: String, personaSuffix: String): String = if (personaSuffix.isEmpty()) persona else "$persona $personaSuffix"
}

/** The `personaSuffix` rules (issue #747): the UAT batch discriminator, empty for the ordinary user. */
@Suppress("ConstPropertyName")
object PERSONASUFFIX {
    /** At most this many characters -- `1`, `2`, `A`, `B`, or a short word that means something to the creator. */
    const val maxLength = 8

    /**
     * Whether [personaSuffix] is one a user may be created with: empty (the ordinary user), or up to [maxLength]
     * ASCII letters, digits, and underscores -- the tightly controlled charset an id has, so two personaSuffixes that
     * look alike to a person cannot name different users. A tombstone's `deleted-<userId>` is not one of
     * these and never goes through this check: it is written by the delete, not chosen.
     */
    fun isValid(personaSuffix: String): Boolean =
        personaSuffix.length <= maxLength && personaSuffix.all { it in 'a'..'z' || it in 'A'..'Z' || it in '0'..'9' || it == '_' }
}
